import { isAiExportLink, linkifyAiExports } from './aiChatService'

/**
 * A markdown reply as plain text, for copying, sharing and reading aloud:
 * formatting marks removed, links as "label (url)". Download links to the
 * portal API are dropped - they only work inside the chat for this user.
 */
export const toPlainText = (markdown: string): string =>
    linkifyAiExports(markdown)
        .replace(
            /\[([^\]]+)\]\(([^)\s]+)\)/g,
            (_match: string, label: string, href: string) =>
                isAiExportLink(href) ? '' : `${label} (${href})`,
        )
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/(\*\*|__)(.+?)\1/g, '$2')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/^(\s*)[-*+]\s+/gm, '$1• ')
        // Gaps left by removed links; indentation at line start is kept.
        .replace(/(\S)[ \t]{2,}/g, '$1 ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()

/**
 * Plain text in pieces for the speech server: the first sentence alone (so
 * playback starts quickly), then sentences grouped up to `max` characters.
 * Sentence ends include the Devanagari danda (।).
 */
export const splitForSpeech = (plain: string, max = 400): string[] => {
    const sentences = plain
        .split(/(?<=[.!?।])\s+|\n+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .flatMap((s) => {
            // A sentence longer than max is cut at spaces.
            if (s.length <= max) return [s]
            const words = s.split(/\s+/)
            const parts: string[] = []
            let current = ''
            for (const word of words) {
                if (current && current.length + word.length + 1 > max) {
                    parts.push(current)
                    current = word
                } else {
                    current = current ? `${current} ${word}` : word
                }
            }
            if (current) parts.push(current)
            return parts
        })
    // The server picks one voice per piece from its script, so sentences in
    // different scripts (English, Tamil, Hindi...) are never grouped together.
    const pieces: string[] = []
    let lastScript = ''
    for (const sentence of sentences) {
        const script = scriptOf(sentence)
        const last = pieces.length > 1 ? pieces[pieces.length - 1] : undefined
        if (
            last !== undefined &&
            script === lastScript &&
            last.length + sentence.length + 1 <= max
        ) {
            pieces[pieces.length - 1] = `${last} ${sentence}`
        } else {
            pieces.push(sentence)
        }
        lastScript = script
    }
    return pieces
}

/** The Unicode block of the sentence's first Indian-script letter, or "latin". */
const scriptOf = (text: string): string => {
    for (const char of text) {
        const code = char.codePointAt(0) ?? 0
        if (code >= 0x0900 && code <= 0x0d7f) return `indic-${code >> 7}`
    }
    return 'latin'
}

/** Cut to a length that fits a share URL, ending with an ellipsis when cut. */
export const truncateForShare = (text: string, max: number): string =>
    text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`
